import Button from '@carvana/ds-button';
import React from 'react';
import SomethingWentWrongModal from '|/components/carvana-ds/something-went-wrong-modal/something-went-wrong-modal';
import { Consents } from './consents';
import { DigitalWallets } from './digital-wallets';
import { ImpersonationBlockedBanner } from './impersonation-blocked-banner';
import styles from './pay-at-order.module.css';
import { PaymentDetailsCard } from './payment-details-card';
import { PlaidModal } from './plaid-modal';
import { usePayAtOrder } from './use-pay-at-order';

interface PayAtOrderProps {
  onSubmit: () => Promise<boolean>;
  editPaymentMethod: () => void;
}

export const PayAtOrder = ({ onSubmit, editPaymentMethod }: PayAtOrderProps) => {
  const {
    paymentSelection,
    requestForPaymentAmount,
    isCardEligible,
    isSubmitting,
    isButtonDisabled,
    isPlaidModalOpen,
    isUserBeingImpersonated,
    shouldUseDigitalWallet,
    shouldShowConsents,
    shouldShowImpersonationText,
    shouldInitiateRequestForPayment,
    hasCustomerConfirmedSufficientFunds,
    buttonText,
    setHasCustomerConfirmedSufficientFunds,
    setIsDigitalWalletSupported,
    setIsPlaidModalOpen,
    onCardAdded,
    handleError,
    handleSubmit,
    onPlaidExit,
    submitPayAtOrder
  } = usePayAtOrder({ onSubmit });

  return (
    <>
      <div className={styles.container}>
        <div className={styles.contentContainer}>
          <PaymentDetailsCard />

          <div className={styles.belowCard}>
            {shouldShowConsents && (
              <Consents
                disableClickOnSufficientFundsCheckbox={isSubmitting}
                hasCustomerConfirmedSufficientFunds={hasCustomerConfirmedSufficientFunds}
                setHasCustomerConfirmedSufficientFunds={setHasCustomerConfirmedSufficientFunds}
              />
            )}

            {shouldShowImpersonationText && (
              <ImpersonationBlockedBanner
                shouldUseDigitalWallet={shouldUseDigitalWallet}
                shouldInitiateRequestForPayment={shouldInitiateRequestForPayment}
                selectionType={paymentSelection?.selectionType}
              />
            )}

            {shouldUseDigitalWallet && !isUserBeingImpersonated && (
              <DigitalWallets
                digitalWallet={paymentSelection?.digitalWallet}
                isCardEligible={isCardEligible}
                editPaymentMethod={editPaymentMethod}
                onSupportChange={setIsDigitalWalletSupported}
                onCardAdded={onCardAdded}
                onSetupConfirmed={submitPayAtOrder}
                onError={handleError}
              />
            )}

            {!shouldUseDigitalWallet && (
              <div className={styles.buttonContainer}>
                <Button
                  variant="primary"
                  size="md"
                  width="full"
                  disabled={isButtonDisabled}
                  isLoading={isSubmitting}
                  onClick={() => {
                    void submitPayAtOrder();
                  }}
                  data-analytics-id="pp_complete_purchase_pao_submit"
                >
                  {buttonText}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <PlaidModal
        isOpen={isPlaidModalOpen}
        onClose={() => {
          setIsPlaidModalOpen(false);
        }}
        paymentAmount={requestForPaymentAmount ?? 0}
        onSuccess={() => {
          handleSubmit().finally(() => {
            setIsPlaidModalOpen(false);
          });
        }}
        onExit={() => {
          setIsPlaidModalOpen(false);
          onPlaidExit();
        }}
        onError={() => {
          setIsPlaidModalOpen(false);
          handleError();
        }}
      />

      <SomethingWentWrongModal text="collecting your payment" />
    </>
  );
};
